/*
 * Featured collection swiper: a plain, centred, looping Swiper track
 * (replaces the old 3D coverflow). Slide widths, the gap and every
 * breakpoint live in component-featured-collection-swiper.css. This file
 * only reads --eg-fs-gap from the section root and hands it to Swiper,
 * which needs the gap as a number.
 *
 * The loop needs spare slides either side of the centred one. Tiles are
 * at least 25.07% of the track on desktop, so no more than ~4 ever show at
 * once and MIN_SLIDES always fills the view; smaller collections get
 * copies of their slides up to that. The dots are drawn per product, not
 * per slide, so the copies don't add dots.
 */
(() => {

    const ROOT = '[data-eg-featured-swiper]';
    const MIN_SLIDES = 6;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /** @param {HTMLElement} root */
    function readGap(root) {
        return parseFloat(getComputedStyle(root).getPropertyValue('--eg-fs-gap')) || 0;
    }

    /**
     * Append whole copies of the slide set (keeps the product order) until
     * there are MIN_SLIDES. Copies drop their ids so quick-add form ids stay
     * unique; the variant select keeps its name via aria-label.
     * @param {HTMLElement} wrapper
     */
    function fillLoop(wrapper) {
        const originals = [...wrapper.children];
        while (wrapper.children.length < MIN_SLIDES) {
            for (const slide of originals) {
                const copy = slide.cloneNode(true);
                for (const label of copy.querySelectorAll('label[for]')) {
                    label.nextElementSibling?.setAttribute('aria-label', label.textContent.trim());
                    label.removeAttribute('for');
                }
                for (const el of copy.querySelectorAll('[id]')) el.removeAttribute('id');
                wrapper.append(copy);
            }
        }
    }

    /**
     * One dot per product: the real index modulo the product count.
     * @param {number} count
     */
    function renderDots(count) {
        return (swiper) => {
            const active = swiper.realIndex % count;
            let html = '';
            for (let i = 0; i < count; i++) {
                const current = i === active;
                html += `<button type="button" class="swiper-pagination-bullet${current ? ' swiper-pagination-bullet-active' : ''}" data-dot="${i}" aria-label="${i + 1} / ${count}"${current ? ' aria-current="true"' : ''}></button>`;
            }
            return html;
        };
    }

    /** @param {HTMLElement} root */
    function initSwiper(root) {
        const track = root.querySelector('.swiper');
        if (!track || track.swiper || typeof Swiper === 'undefined') return;

        const wrapper = track.querySelector('.swiper-wrapper');
        const count = wrapper.children.length;
        if (count > 1) fillLoop(wrapper);

        const dots = root.querySelector('.swiper-pagination');

        const swiper = new Swiper(track, {
            slidesPerView: 'auto',
            centeredSlides: true,
            loop: count > 1,
            spaceBetween: readGap(root),
            speed: reduceMotion.matches ? 0 : 600,
            grabCursor: true,
            watchOverflow: true,

            navigation: {
                prevEl: root.querySelector('.eg-featured-swiper__nav--prev'),
                nextEl: root.querySelector('.eg-featured-swiper__nav--next'),
            },

            pagination: {
                el: dots,
                type: 'custom',
                renderCustom: renderDots(count),
            },

            on: {
                // --eg-fs-gap changes at the mobile breakpoint.
                resize(instance) {
                    const gap = readGap(root);
                    if (gap === instance.params.spaceBetween) return;
                    instance.params.spaceBetween = gap;
                    instance.update();
                },
            },
        });

        dots?.addEventListener('click', (event) => {
            const dot = event.target.closest('[data-dot]');
            if (dot) swiper.slideToLoop(Number(dot.dataset.dot));
        });
    }

    /** @param {ParentNode} scope */
    function initAll(scope = document) {
        for (const root of scope.querySelectorAll(ROOT)) initSwiper(root);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initAll());
    } else {
        initAll();
    }

    document.addEventListener('shopify:section:load', (event) => initAll(event.target));

    document.addEventListener('shopify:section:unload', (event) => {
        for (const track of event.target.querySelectorAll(`${ROOT} .swiper`)) {
            track.swiper?.destroy(true, true);
        }
    });

})();
